import { useRef } from "react";
import { UploadCloud, FileCheck2, X, FileWarning } from "lucide-react";

/**
 * FileUpload
 * props:
 *  - label, hint
 *  - required (bool)
 *  - multiple (bool)  — when true, expects `files: File[]` and calls onChange with array
 *  - value: File | File[] | null
 *  - onChange(file|files)
 *  - accept
 *  - maxCount (if multiple)
 */
export default function FileUpload({
  label,
  hint,
  required = false,
  multiple = false,
  value,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png",
  maxCount = 3,
  error,
  testId,
}) {
  const inputRef = useRef(null);

  const files = multiple ? (value || []) : (value ? [value] : []);
  const hasFiles = files.length > 0;
  const done = multiple ? files.length === maxCount : hasFiles;

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    for (const f of arr) {
      if (f.size > 10 * 1024 * 1024) {
        onChange(multiple ? [] : null, "Each file must be ≤ 10MB.");
        return;
      }
    }
    if (multiple) {
      const merged = [...(value || []), ...arr].slice(0, maxCount);
      onChange(merged);
    } else {
      onChange(arr[0]);
    }
  };

  const removeAt = (idx) => {
    if (multiple) {
      const next = [...(value || [])];
      next.splice(idx, 1);
      onChange(next);
    } else {
      onChange(null);
    }
  };

  return (
    <div className="w-full h-full">
      <label className="mb-1.5 flex items-center justify-between text-[12px] font-semibold uppercase tracking-[0.14em] text-[hsl(220,30%,28%)]">
        <span>
          {label} {required && <span className="text-[hsl(356,78%,52%)]">*</span>}
        </span>
        {hint && <span className="text-[11px] font-medium normal-case tracking-normal text-[hsl(220,15%,46%)]">{hint}</span>}
      </label>

      <div
        className={`file-tile h-full flex ${done ? "done" : ""} ${error ? "err" : ""}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        data-testid={testId}
      >
        {!hasFiles && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-3 text-left"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[hsl(220,22%,85%)] bg-white text-[hsl(222,82%,46%)]">
              <UploadCloud className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[hsl(220,45%,12%)]">
                {multiple ? `Upload up to ${maxCount} images` : "Upload document"}
              </div>
              <div className="text-xs text-[hsl(220,15%,46%)]">
                Drag & drop or click to browse — PDF, JPG, PNG · max 10MB
              </div>
            </div>
          </button>
        )}

        {hasFiles && (
          <div className="space-y-2">
            {files.map((f, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 rounded-lg border border-[hsl(220,22%,88%)] bg-white px-3 py-2"
              >
                <FileCheck2 className="h-4 w-4 shrink-0 text-[hsl(152,60%,38%)]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-[hsl(220,45%,12%)]">{f.name}</div>
                  <div className="text-[11px] text-[hsl(220,15%,46%)]">{(f.size / 1024).toFixed(1)} KB</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="rounded-md p-1 text-[hsl(220,15%,46%)] hover:bg-[hsl(220,30%,96%)] hover:text-[hsl(356,78%,45%)]"
                  aria-label="Remove file"
                  data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            {multiple && files.length < maxCount && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-xs font-semibold text-[hsl(222,82%,46%)] hover:underline"
              >
                + Add more ({files.length}/{maxCount})
              </button>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {error && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[12px] font-medium text-[hsl(356,78%,45%)]">
          <FileWarning className="h-3.5 w-3.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
